/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { GetToDosQuery } from '@/gql/graphql';
import dayjs from 'dayjs';
import {
  Abteilung,
  Arbeitsbereich,
} from '../../components/Table/ToDoTable/entities/Arbeitsbereich';
import { Arbeitszeit } from '../../components/Table/ToDoTable/entities/Arbeitszeit';
import { Mitarbeiter } from '../../components/Table/ToDoTable/entities/Mitarbeiter';
import { Zeitstempel } from '../../components/Table/ToDoTable/entities/Zeitstempel';
import { Zuschlag } from '../../components/Table/ToDoTable/entities/Zuschlag';
import { Schicht } from '../../components/Table/ToDoTable/entities/Schicht';
import { Pause } from '@/domain/arbeitszeiten/entities';
import {
  Termin,
  toTerminWiederholung,
} from '@/domain/arbeitszeiten/entities/Termin';

type ToDo = GetToDosQuery['toDos'][number];
type ToDoArbeitszeiten = Extract<ToDo, { __typename?: 'ToDosArbeitszeiten' }>;

function isToDoArbeitszeit(toDo: ToDo): toDo is ToDoArbeitszeiten {
  return toDo.__typename === 'ToDosArbeitszeiten';
}

export function getToDosArbeitszeiten({ data }: { data: GetToDosQuery }) {
  const toDosArbeitszeiten = data.toDos
    .filter(isToDoArbeitszeit)
    .map((toDoArbeitszeit) => {
      const arbeitsbereich = new Arbeitsbereich({
        id: toDoArbeitszeit.arbeitsbereich.id,
        name: toDoArbeitszeit.arbeitsbereich.name,
        markierung: toDoArbeitszeit.arbeitsbereich.markierung,
        abteilungen: (toDoArbeitszeit.arbeitsbereich.abteilungen ?? []).map(
          (abteilung) => {
            return new Abteilung({
              id: abteilung.id,
            });
          },
        ),
      });
      const zuschlaege = toDoArbeitszeit.zuschlaege.map((zuschlag) => {
        if (zuschlag.betrag.__typename === 'arbeitszeiten_zuschlaege_betrag') {
          const betrag = zuschlag.betrag;
          return new Zuschlag({
            name: zuschlag.name,
            prozent: zuschlag.prozent,
            betrag: betrag.betrag,
            dauer: zuschlag.dauer,
          });
        }
        return new Zuschlag({
          name: zuschlag.name,
          prozent: zuschlag.prozent,
          dauer: zuschlag.dauer,
        });
      });

      const termin = toDoArbeitszeit.termin
        ? new Termin(
            toDoArbeitszeit.termin.titel ?? undefined,
            toTerminWiederholung(toDoArbeitszeit.termin.wiederholung),
            toDoArbeitszeit.termin.id ?? undefined,
            toDoArbeitszeit.termin.bildUrl ?? undefined,
          )
        : undefined;

      const newToDoArbeitszeit = new Arbeitszeit({
        mitarbeiter: new Mitarbeiter({
          id: toDoArbeitszeit.mitarbeiter.id,
          vorname: toDoArbeitszeit.mitarbeiter.vorname,
          nachname: toDoArbeitszeit.mitarbeiter.nachname,
          arbeitsbereich: arbeitsbereich,
          imgUrl: toDoArbeitszeit.mitarbeiter.bildUrl ?? undefined,
          extern: toDoArbeitszeit.mitarbeiter.extern,
        }),
        arbeitsbereich: arbeitsbereich,
        id: toDoArbeitszeit.id,
        beginn: toDoArbeitszeit.beginn
          ? new Zeitstempel({
              abweichung: toDoArbeitszeit.beginn.abweichung ?? undefined,
              zeitpunkt: toDoArbeitszeit.beginn.zeitpunkt
                ? dayjs(toDoArbeitszeit.beginn.zeitpunkt)
                : undefined,
              schicht: toDoArbeitszeit.beginn.schicht
                ? new Schicht({
                    id: toDoArbeitszeit.beginn.schicht.id,
                    beginn: dayjs(toDoArbeitszeit.beginn.schicht.beginn),
                    ende: dayjs(toDoArbeitszeit.beginn.schicht.ende),
                  })
                : undefined,
            })
          : undefined,
        ende: toDoArbeitszeit.ende
          ? new Zeitstempel({
              abweichung: toDoArbeitszeit.ende.abweichung ?? undefined,
              zeitpunkt: toDoArbeitszeit.ende.zeitpunkt
                ? dayjs(toDoArbeitszeit.ende.zeitpunkt)
                : undefined,
              schicht: toDoArbeitszeit.ende.schicht
                ? new Schicht({
                    id: toDoArbeitszeit.ende.schicht.id,
                    beginn: dayjs(toDoArbeitszeit.ende.schicht.beginn),
                    ende: dayjs(toDoArbeitszeit.ende.schicht.ende),
                  })
                : undefined,
            })
          : undefined,
        kommen: toDoArbeitszeit.kommen?.zeitpunkt
          ? new Zeitstempel({
              abweichung: toDoArbeitszeit.kommen.abweichung ?? undefined,
              zeitpunkt: dayjs(toDoArbeitszeit.kommen.zeitpunkt),
            })
          : undefined,
        gehen: toDoArbeitszeit.gehen?.zeitpunkt
          ? new Zeitstempel({
              abweichung: toDoArbeitszeit.gehen.abweichung ?? undefined,
              zeitpunkt: dayjs(toDoArbeitszeit.gehen.zeitpunkt),
            })
          : undefined,
        pausen: toDoArbeitszeit.pausen
          ? toDoArbeitszeit.pausen.map(
              (pause) =>
                new Pause({
                  id: pause.id ?? undefined,
                  bezahlt: pause.bezahlt,
                  art: pause.art,
                  dauer: pause.dauer ?? undefined,
                  beginn: pause.beginn ? new Date(pause.beginn) : undefined,
                  ende: pause.ende ? new Date(pause.ende) : undefined,
                }),
            )
          : [],
        bemerkung: toDoArbeitszeit.bemerkung ?? undefined,
        zuschlaege: zuschlaege,
        termin,
      });
      return newToDoArbeitszeit;
    });
  return toDosArbeitszeiten;
}
